/**
 * 认证检查守卫
 *
 * 每次导航时检查目标路由是否需要认证，
 * 普通未认证用户将被重定向到外部登录页面；开发自动登录失败时停留在内部错误页。
 * 包含重定向循环检测以防止配置错误导致的无限跳转。
 */

import type {
  NavigationGuardNext,
  NavigationGuardWithThis,
  RouteLocationNormalized,
} from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { apiConfig } from '@/services/api/config';
import {
  externalAuthRedirectService,
  type LoginRedirectUrlResolution,
} from '@/services/auth/ExternalAuthRedirectService';
import { isRedirectLoop, clearRedirectLoop } from './redirect-loop-guard';
import {
  AUTH_ERROR_REASON,
  AUTH_ERROR_REASON_QUERY_KEY,
  type AuthErrorReason,
} from './auth-error-reason';

/**
 * 认证检查守卫依赖。
 *
 * 使用依赖注入隔离浏览器跳转、外部登录地址解析与全局 store，
 * 确保不同认证异常分支都可以被单元测试稳定覆盖。
 */
export interface AuthCheckGuardDeps {
  /** 读取当前是否已经完成认证。 */
  getIsAuthenticated: () => boolean;

  /** 读取开发环境自动登录开关。 */
  getAutoLoginEnabled: () => boolean;

  /** 读取开发环境自动登录是否已经失败。 */
  getHasDevelopmentAutoLoginFailure: () => boolean;

  /** 读取当前站点 origin，用于构造外部登录后的回跳地址。 */
  getCurrentOrigin: () => string;

  /** 解析外部登录跳转地址。 */
  resolveLoginRedirectUrl: (returnUrl: string) => LoginRedirectUrlResolution;

  /** 执行浏览器外部跳转。 */
  assignLocation: (url: string) => void;

  /** 判断是否已经出现外部登录重定向循环。 */
  isRedirectLoop: () => boolean;

  /** 清除重定向循环检测状态。 */
  clearRedirectLoop: () => void;

  /** 设置当前页面标题。 */
  setDocumentTitle: (title: string) => void;
}

/**
 * 将外部登录地址解析失败结果转换为认证错误页原因。
 *
 * @param resolution 外部登录地址解析结果。
 * @returns 认证错误页可识别的原因。
 */
function resolveExternalAuthErrorReason(
  resolution: Exclude<LoginRedirectUrlResolution, { status: 'ready' }>,
): AuthErrorReason {
  if (resolution.status === 'invalid-config') {
    return AUTH_ERROR_REASON.InvalidExternalAuthConfig;
  }

  return AUTH_ERROR_REASON.MissingExternalAuthConfig;
}

/**
 * 认证检查守卫。
 *
 * 职责：
 * 1. 设置页面标题
 * 2. 检查需要认证的路由，未认证时按当前登录模式选择内部错误页或外部登录
 * 3. 检测重定向循环，循环时跳转到 auth-error 页面
 * 4. 预留 guestOnly 逻辑
 *
 * @param deps 认证检查守卫依赖。
 * @returns Vue Router 可直接使用的导航守卫函数。
 */
export function createAuthCheckGuard(deps: AuthCheckGuardDeps) {
  return (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: NavigationGuardNext,
  ): void => {
    // 页面标题由守卫统一写入，避免各页面重复处理 document 依赖。
    if (to.meta.title) {
      deps.setDocumentTitle(to.meta.title as string);
    }

    // 需要认证的路由在未登录时必须先处理开发自动登录失败，再考虑外部登录。
    if (to.matched.some(record => record.meta.requiresAuth)) {
      if (!deps.getIsAuthenticated()) {
        if (deps.getAutoLoginEnabled() && deps.getHasDevelopmentAutoLoginFailure()) {
          /**
           * 开发自动登录开启时，认证失败是本地开发配置或凭据问题。
           * 这里走内部错误页，避免把用户继续送到外部登录页而掩盖真实失败原因。
           */
          next({
            name: 'auth-error',
            query: {
              [AUTH_ERROR_REASON_QUERY_KEY]: AUTH_ERROR_REASON.DevAutoLoginFailed,
            },
          });
          return;
        }

        // 循环检测优先于外部登录跳转，避免错误配置导致页面反复离开 SPA。
        if (deps.isRedirectLoop()) {
          next({ name: 'auth-error' });
          return;
        }

        const returnUrl = deps.getCurrentOrigin() + to.fullPath;
        const loginUrlResolution = deps.resolveLoginRedirectUrl(returnUrl);
        if (loginUrlResolution.status !== 'ready') {
          /**
           * 外部认证入口缺失或无效时不能落到默认 localhost。
           * 这里进入统一认证错误页，让用户看到明确的配置提示。
           */
          next({
            name: 'auth-error',
            query: {
              [AUTH_ERROR_REASON_QUERY_KEY]: resolveExternalAuthErrorReason(loginUrlResolution),
            },
          });
          return;
        }

        deps.assignLocation(loginUrlResolution.url);
        return;
      }
    }

    // 预留：仅访客可访问的路由。
    if (to.matched.some(record => record.meta.guestOnly)) {
      if (deps.getIsAuthenticated()) {
        // 已认证用户重定向到默认页面。
        // next('/');
      }
    }

    // 认证通过或无需认证，清除循环检测状态。
    deps.clearRedirectLoop();
    next();
  };
}

export const authCheckGuard: NavigationGuardWithThis<undefined> = (to, from, next) => {
  const authStore = useAuthStore();
  const guard = createAuthCheckGuard({
    getIsAuthenticated: () => authStore.isAuthenticated,
    getAutoLoginEnabled: () => apiConfig.devAuth.autoLoginEnabled,
    getHasDevelopmentAutoLoginFailure: () => authStore.hasDevelopmentAutoLoginFailure,
    getCurrentOrigin: () => window.location.origin,
    resolveLoginRedirectUrl: returnUrl =>
      externalAuthRedirectService.resolveLoginRedirectUrl(returnUrl),
    assignLocation: (url) => {
      window.location.href = url;
    },
    isRedirectLoop,
    clearRedirectLoop,
    setDocumentTitle: (title) => {
      document.title = title;
    },
  });

  return guard(to, from, next);
};
